import buildComms from '@app/application/communication/sections/communication/store/comms'
import { createStore } from 'vuex'
import users from '@app/application/communication/sections/communication/store/users'
import contexts from '@app/application/communication/sections/communication/store/contexts'
import checks from '@app/application/communication/sections/communication/store/checks'
import attachments from '@app/application/communication/sections/communication/store/attachments'
import comments from '@app/application/communication/sections/communication/store/comments'
import ui from '@app/application/communication/sections/communication/store/ui'
import remarks from '@app/application/communication/sections/communication/store/remarks'
import remarkTags from '@app/application/communication/sections/communication/store/remark-tags'
import sections from '@app/application/communication/screens/inbox/store/sections'
import notifications from '@app/application/communication/screens/inbox/store/notifications'
import dropdown from '@app/shared/vue/store/modules/dropdown'
import * as mutationTypes from './mutation-types'
import * as PrepareApi from '@app/application/client_meetings/api/prepare'
import * as TitlesApi from '@app/application/client_meetings/api/titles'
import { SfLog } from '@app/adapters/logger'

export default function createMeetingPreparationStore(endpointParams) {
  const { state, getters, mutations, actions } = buildComms(
    endpointParams,
    {},
    PrepareApi.getCommunication
  )

  const store = createStore({
    modules: {
      users,
      contexts,
      checks,
      attachments,
      comments: comments,
      ui,
      remarks,
      sections,
      // Registered so the communication drawer (shown in presentation mode) has
      // the same store shape as the standalone comms store it relies on.
      notifications,
      dropdown,
    },
    state() {
      return {
        ...state(),
        loading: {
          companies: false,
          mentionUsernames: false,
          communicationItem: false,
        },
        order: [],
        titles: [],
        settingOrder: false,
        showSidebar: false,
        insertTitlesBeforeId: null,
      }
    },
    getters,
    mutations: {
      ...mutations,
      [mutationTypes.SET_LOADING](state, { type, value }) {
        state.loading[type] = value
      },
      [mutationTypes.SET_ORDER](state, order) {
        state.order = order
      },
      [mutationTypes.SET_SETTING_ORDER](state, value) {
        state.settingOrder = value
      },
      [mutationTypes.INSERT_TITLES_BEFORE](state, id) {
        state.insertTitlesBeforeId = id
      },
      [mutationTypes.SET_TITLES](state, titles) {
        state.titles = titles
      },
      [mutationTypes.CREATE_TITLE](state, title) {
        state.titles = Object.values(state.titles).concat([title])
      },
      [mutationTypes.UPDATE_TITLE](state, { id, content, size }) {
        state.titles = Object.values(state.titles).map((t) => {
          if (t.id === id) {
            return {
              ...t,
              content,
              size,
            }
          }

          return t
        })
      },
      [mutationTypes.REMOVE_TITLE](state, id) {
        state.titles = Object.values(state.titles).filter((t) => t.id !== id)
      },
    },
    actions: {
      ...actions,
      setInitialOrder({ commit }, order) {
        commit(mutationTypes.SET_ORDER, order)
      },
      async setOrder({ commit, state }, newOrder) {
        commit(mutationTypes.SET_ORDER, newOrder)
        commit(mutationTypes.SET_SETTING_ORDER, true)
        try {
          await PrepareApi.setOrder(newOrder)
        } catch (e) {
          SfLog.warn('Failed to set order of client meeting', {
            extra: {
              error: e,
              order: state.order,
            },
          })
        } finally {
          commit(mutationTypes.SET_SETTING_ORDER, false)
        }
      },
      setInsertBeforeId({ commit }, insertBeforeId) {
        commit(mutationTypes.INSERT_TITLES_BEFORE, insertBeforeId)
      },
      async createTitle({ commit }, { content, size, insertBeforeId }) {
        try {
          const { title } = await TitlesApi.create(content, size)

          const newTitle = {
            kind: 'title',
            id: title.id,
            content: title.content,
            size: title.size,
          }

          commit(mutationTypes.CREATE_TITLE, newTitle)
          commit(mutationTypes.INSERT_TITLES_BEFORE, insertBeforeId)

          return newTitle
        } catch (e) {
          SfLog.warn('Failed to create title', {
            extra: {
              error: e,
              content,
              size,
            },
          })
          throw e
        }
      },
      async updateTitle({ commit }, { id, content, size }) {
        try {
          await TitlesApi.update(id, content, size)
          commit(mutationTypes.UPDATE_TITLE, { id, content, size })
        } catch (e) {
          SfLog.warn('Failed to update title', {
            extra: {
              error: e,
              content,
              size,
            },
          })
          throw e
        }
      },
      async removeTitle({ commit }, id) {
        try {
          await TitlesApi.remove(id)
          commit(mutationTypes.REMOVE_TITLE, id)
        } catch (e) {
          SfLog.warn('Failed to remove title', {
            extra: {
              error: e,
              id,
            },
          })
          throw e
        }
      },
    },
  })

  store.registerModule('remarkTags', remarkTags, { preserveState: true })

  return store
}
